import NativeEventEmitterModule from 'react-native/Libraries/EventEmitter/NativeEventEmitter'
import { NativeModules } from 'react-native'

type NoraAudioPlaybackCallbacks = {
  onEnd?: () => void
  onError?: (error: Error) => void
}

type NativeSubscription = {
  remove: () => void
}

type NativeEventEmitterConstructor = new (nativeModule: unknown) => {
  addListener: (eventName: string, listener: (payload: unknown) => void) => NativeSubscription
}

type NativeNoraAudioPlaybackModule = {
  play: (filePath: string) => Promise<void>
  stop: () => Promise<void>
}

const nativeModule = NativeModules.NoraAudioPlayback as NativeNoraAudioPlaybackModule | null
const NativeEventEmitter = (NativeEventEmitterModule as unknown) as NativeEventEmitterConstructor

// NativeEventEmitter throws on iOS when constructed with a null module, so build it
// lazily and only once the module is known to be linked.
let emitterInstance: InstanceType<NativeEventEmitterConstructor> | null = null

const getEmitter = (module: NativeNoraAudioPlaybackModule) => {
  if (!emitterInstance) {
    emitterInstance = new NativeEventEmitter(module)
  }

  return emitterInstance
}

let activeFilePath: string | null = null
let subscriptions: NativeSubscription[] = []

const removeSubscriptions = () => {
  subscriptions.forEach((subscription) => subscription.remove())
  subscriptions = []
}

const payloadFilePath = (payload: unknown) => {
  if (payload && typeof payload === 'object' && 'filePath' in payload) {
    return String((payload as { filePath?: unknown }).filePath)
  }

  return null
}

const payloadMessage = (payload: unknown) => {
  if (payload && typeof payload === 'object' && 'message' in payload) {
    return String((payload as { message?: unknown }).message)
  }

  return 'Nora speech playback failed'
}

const unavailableError = () =>
  new Error('Nora audio playback native module is unavailable. Rebuild the app.')

export const NoraAudioPlayback = {
  play: async (filePath: string, callbacks: NoraAudioPlaybackCallbacks = {}) => {
    if (!nativeModule) {
      const error = unavailableError()
      throw error
    }

    removeSubscriptions()
    activeFilePath = filePath

    const emitter = getEmitter(nativeModule)

    subscriptions = [
      emitter.addListener('noraPlaybackEnded', (payload: unknown) => {
        if (payloadFilePath(payload) !== activeFilePath) {
          return
        }

        activeFilePath = null
        removeSubscriptions()
        callbacks.onEnd?.()
      }),
      emitter.addListener('noraPlaybackError', (payload: unknown) => {
        if (payloadFilePath(payload) !== activeFilePath) {
          return
        }

        activeFilePath = null
        removeSubscriptions()
        callbacks.onError?.(new Error(payloadMessage(payload)))
      }),
    ]

    try {
      await nativeModule.play(filePath)
    } catch (error) {
      activeFilePath = null
      removeSubscriptions()
      // Start failures arrive as a rejection, not as a noraPlaybackError event, and the
      // listeners are already gone — so onError has to be invoked here to fire at all.
      callbacks.onError?.(error instanceof Error ? error : new Error(payloadMessage(error)))
      throw error
    }
  },

  stop: async () => {
    activeFilePath = null
    removeSubscriptions()
    if (!nativeModule) {
      return
    }

    await nativeModule.stop()
  },
}
